import React, { useRef, useEffect } from "react";
import { Box, Button, Typography, List, ListItem, IconButton, Tooltip, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ACCEPTED_FORMATS = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
const EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];
const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 Mo
const MAX_FILES = 15;

const UploadFiles = ({ files, setFiles, error, setError }) => {
    const inputRef = useRef();
    const theme = useTheme();

    // Reset the file input whenever files array is cleared
    useEffect(() => {
        if (files.length === 0 && inputRef.current) {
            inputRef.current.value = "";
        }
    }, [files]);

    const handleChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        // Clear input so that selecting same file again triggers onChange
        e.target.value = "";

        const newFiles = [...files, ...selectedFiles];
        setFiles(newFiles);

        // 2) Validate formats
        for (let file of selectedFiles) {
            if (
                !ACCEPTED_FORMATS.includes(file.type) &&
                !EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))
            ) {
                setError("Format de document non autorisé");
                return;
            }
        }

        // 3) Check max count
        if (newFiles.length > MAX_FILES) {
            setError(`Le nombre maximal de fichiers autorisé (${MAX_FILES}) est dépassé`);
        } else {
            // only clear error if size also ok
            const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
            if (totalSize <= MAX_TOTAL_SIZE) {
                setError("");
            }
        }

        // 4) Check total size
        const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
            setError(`La taille maximale totale autorisée (10 Mo) est dépassée`);
        } else if (newFiles.length <= MAX_FILES) {
            setError("");
        }
    };

    const handleRemove = (index) => {
        const updated = [...files];
        updated.splice(index, 1);
        setFiles(updated);

        // Re-check count & size once one is removed
        const totalSize = updated.reduce((sum, f) => sum + f.size, 0);
        if (updated.length <= MAX_FILES && totalSize <= MAX_TOTAL_SIZE) {
            setError("");
        } else if (updated.length > MAX_FILES) {
            setError(`Le nombre maximal de fichiers autorisé (${MAX_FILES}) est dépassé`);
        } else if (totalSize > MAX_TOTAL_SIZE) {
            setError(`La taille maximale totale autorisée (10 Mo) est dépassée`);
        }
    };

    const lightBg = '#f9f9f9';
    const darkBg = theme.palette.background.paper;
    const lightHover = '#eaeaea';
    const darkHover = theme.palette.action.hover;

    return (
        <Box sx={{ mb: 2 }}>
            <input
                ref={inputRef}
                multiple
                type="file"
                style={{ display: "none" }}
                accept={EXTENSIONS.join(',')}
                onChange={handleChange}
            />

            <Button
                variant="contained"
                onClick={() => inputRef.current.click()}
                sx={{ mb: 1, textTransform: "none" }}
            >
                Ajouter des fichiers
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                {/* pull the icon up so it sits next to the first bullet */}
                <InfoOutlinedIcon color="action" sx={{ mr: 0.5, mt: '4px' }} />

                {/* use a real UL for bullets */}
                <Box
                    component="ul"
                    sx={{
                        m: 0,
                        p: 0,
                        pl: 2,
                        listStyleType: 'disc',
                    }}
                >
                    <Box component="li" sx={{ p: 0, mb: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                            Extensions autorisées : PDF, JPG, JPEG, PNG, DOCX
                        </Typography>
                    </Box>
                    <Box component="li" sx={{ p: 0, mb: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                            Nombre max : {MAX_FILES} fichiers
                        </Typography>
                    </Box>
                    <Box component="li" sx={{ p: 0 }}>
                        <Typography variant="body2" color="textSecondary">
                            Taille max totale : 10 Mo
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}

            <List dense>
                {files.map((file, idx) => (
                    <ListItem
                        key={idx}
                        sx={{
                            mb: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? darkBg : lightBg,
                            borderRadius: 1,
                            px: 2,
                            py: 1,
                            transition: 'background-color 0.3s',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? darkHover : lightHover
                            }
                        }}
                        secondaryAction={
                            <Tooltip title="Supprimer">
                                <IconButton
                                    edge="end"
                                    onClick={() => handleRemove(idx)}
                                    sx={{ color: '#EF595D' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    >
                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export default UploadFiles;
